import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

/**
 * Chapter-specific help copy. Ported from GSA openacr-editor ChapterHelpText.svelte.
 */
const CHAPTER_HELP = {
  about:
    "Fill out general information about your product or service. Please provide as much detail as possible. By describing your product, the webform will update to show only the sections you need to fill out. If you need to change this later, change your response product information questions.",
  wcag_level_a:
    "Provide information about Level A issues with your website or web application.",
  wcag_level_aa:
    "Provide information about how your website or web application fails to meet Level AA requirements.",
  wcag_level_aaa:
    "This section is optional. Consider providing information about Level AAA support in your website or web application. WCAG Level AAA reflect accessibility features that exceed the minimum requirements of Section 508. It is useful to describe how your product or service exceeds Level AA requirements. Additionally, some end-users and some projects may need specific AAA requirements to be meet.",
  functional_performance_criteria:
    "Describe barriers here if they are not covered in other sections. Highlight the impact on different types of disabilities. Optionally, this section may be used to summarize accessibility features described in more detail elsewhere.",
  hardware:
    "If the Information and Communications Technology (ICT) includes hardware, please fill out this section.",
  software:
    "If your software product is a web application or is rendered through a web browser, do not fill out this section. Fill out Tables 1, 2, and 3. Fill out this section if your software product is not web-based. Desktop applications are a common example of software which is not web-based. Software which uses internet communication but is not used via a third-party web browser (e.g., Chrome, Edge, Safari), is not web-based software.",
  support_documentation_and_services:
    "If the support documentation for your product is not web-based (e.g., MS Word, OpenOffice or PDF files), fill out this section.",
};

/** Map chapter labels to CHAPTER_HELP keys when only label is available (e.g. non-WCAG). */
const CHAPTER_LABEL_TO_HELP_KEY = {
  "Functional Performance Criteria": "functional_performance_criteria",
  Hardware: "hardware",
  Software: "software",
  "Support Documentation and Services": "support_documentation_and_services",
};

function openFirstAccordion() {
  const first = document.querySelector("details");
  if (first) first.setAttribute("open", "");
}

export default function ChapterHelpText({ chapterId, chapterLabel }) {
  if (!chapterId && !chapterLabel) return null;

  const candidates = [
    chapterId,
    chapterLabel && CHAPTER_LABEL_TO_HELP_KEY[chapterLabel],
    chapterLabel,
  ].filter(Boolean);
  let helpKey = candidates.find((key) => CHAPTER_HELP[key]);
  let text = helpKey ? CHAPTER_HELP[helpKey] : null;
  if (!text && chapterLabel) {
    const labelMatch = Object.entries(CHAPTER_LABEL_TO_HELP_KEY).find(
      ([label]) => chapterLabel.includes(label)
    );
    if (labelMatch) {
      const [, matchedKey] = labelMatch;
      helpKey = matchedKey;
      text = CHAPTER_HELP[helpKey];
    }
  }
  if (!text) return null;

  const isWcagChapter = helpKey.startsWith("wcag_level_");
  const content = isWcagChapter ? (
    <>
      {text} The default is web-based, and there are optional fields for{" "}
      <Link to="#documentation" onClick={openFirstAccordion}>
        Documentation
      </Link>
      , <Link to="#software" onClick={openFirstAccordion}>Software</Link> and{" "}
      <Link to="#authoring" onClick={openFirstAccordion}>Authoring</Link>. Fill out as appropriate.
    </>
  ) : (
    text
  );

  return <div className="my-4 text-gray-700">{content}</div>;
}

ChapterHelpText.propTypes = {
  chapterId: PropTypes.string,
  chapterLabel: PropTypes.string,
};

ChapterHelpText.defaultProps = {
  chapterId: null,
  chapterLabel: null,
};
